import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Sparkles } from "lucide-react";
import { COURSES, waLink } from "../data/content";
import { Reveal, Eyebrow, EASE } from "./Reveal";

const CourseCard = ({ item, index }) => (
  <motion.div
    data-testid={`course-card-${item.id}`}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: EASE, delay: (index % 2) * 0.1 }}
    className="group relative flex flex-col overflow-hidden rounded-3xl bg-gradient-to-b from-[#2E0C15] to-[#1C060B] border border-[#CBA135]/20 hover:border-[#CBA135]/45 transition-colors duration-500"
  >
    <div className="relative h-52 overflow-hidden">
      <img
        src={item.img}
        alt={item.title}
        className="h-full w-full object-cover opacity-90 transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C060B] via-[#1C060B]/30 to-transparent" />
      {item.exclusive && (
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#CBA135] text-[#21070D] px-3.5 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold">
          <Sparkles size={12} /> Exclusive Batch
        </span>
      )}
    </div>

    <div className="flex flex-1 flex-col p-8">
      <span className="inline-flex items-center gap-2 self-start rounded-full border border-[#EBD08A]/25 px-3 py-1 text-xs text-[#EBD08A]">
        <Clock size={13} /> {item.duration}
      </span>
      <h3 className="mt-4 font-serif font-medium text-[#F9F1E2] text-2xl sm:text-3xl leading-tight">
        {item.title}
      </h3>
      <p className="mt-4 text-[#D8C3B8] text-base leading-relaxed body-serif">
        {item.desc}
      </p>

      <ul className="mt-5 space-y-2.5 body-serif">
        {item.points.map((p, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[#EAD9CB]">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#CBA135]" />
            <span className="text-base">{p}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-7 flex items-center justify-between">
        {item.price === "Enquire" ? (
          <a
            href={waLink(`I want to enquire about the "${item.title}" course. Please share the details.`)}
            target="_blank"
            rel="noreferrer"
            data-testid={`course-enquire-${item.id}`}
            className="group/enq inline-flex flex-col"
          >
            <span className="block text-[10px] uppercase tracking-[0.24em] text-[#B98F52]">
              Investment
            </span>
            <span className="font-serif text-2xl text-[#EBD08A] underline decoration-[#CBA135]/50 underline-offset-4 group-hover/enq:decoration-[#CBA135] transition-colors">
              Enquire
            </span>
          </a>
        ) : (
          <div>
            <span className="block text-[10px] uppercase tracking-[0.24em] text-[#B98F52]">
              Investment
            </span>
            <span className="font-serif text-2xl text-[#F9F1E2]">
              {item.price}
            </span>
          </div>
        )}
        <a
          href={waLink(`Namaste Shreya, I'd like to enrol in the "${item.title}" course. Please share the details.`)}
          target="_blank"
          rel="noreferrer"
          data-testid={`course-enroll-${item.id}`}
          className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold"
        >
          Enroll <ArrowUpRight size={16} />
        </a>
      </div>
    </div>
  </motion.div>
);

export const Courses = () => (
  <section
    id="courses"
    className="relative overflow-hidden bg-[#170509] py-24 sm:py-36 px-6 sm:px-10 md:px-16 grain"
    data-testid="courses"
  >
    <div
      className="absolute inset-0 -z-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(50% 40% at 85% 8%, rgba(203,161,53,0.14), transparent 60%), radial-gradient(45% 35% at 5% 95%, rgba(203,161,53,0.1), transparent 60%)",
      }}
    />
    <div className="relative z-10 max-w-6xl mx-auto">
      <div className="text-center max-w-2xl mx-auto">
        <Reveal>
          <Eyebrow className="text-[#EBD08A]">Courses</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif font-medium text-[#F9F1E2] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Learn the sacred sciences
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 text-[#D8C3B8] text-lg sm:text-xl body-serif">
            Immersive teaching programmes in Tarot, Sri Vidhya, predictive astrology
            and energy healing — from first principles to true mastery.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-8">
        {COURSES.map((item, i) => (
          <CourseCard key={item.id} item={item} index={i} />
        ))}
      </div>
    </div>
  </section>
);
